import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom";
import { PencilIcon } from "@heroicons/react/solid";
import { Formik, Field, Form, useFormikContext } from "formik";
import { useDebounce } from "use-debounce";
import _merge from "lodash/merge";
import _isEmpty from "lodash/isEmpty";
import { getCorporatesForDashboard } from "state/slices/corporatesForDashboard";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import {
  resetVerifyStatus,
  verify,
  verifyForOrganization,
} from "state/slices/verifyInvite";
import { useToast } from "hooks/useToast";
import { ExclamationIcon } from "@heroicons/react/outline";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import ValidationError from "components/lib/Shared/ValidationError";
import Button from "components/lib/Shared/Button";
import { verifyBtnParams } from "components/lib/Shared/Verify/verifyBtnParams";
import {
  setPartialData,
  setVerifiedRegNumber,
  setTransferYourBusinessInitiator,
} from "state/slices/transferYourBusiness";

const Verify = ({
  autoComplete = "off",
  isIdAssociated,
  resetForm = () => null,
  setInviteExistingUser = () => null,
  setShowBusinessDetails,
  companyExists,
  setCompanyExists,
  entityTypeIsAccepted,
  setEntityTypeIsAccepted,
  companyIsTransfered,
  setCompanyIsTransfered,
}) => {
  const {
    name,
    label,
    placeholder,
    type = "string",
    validationSchema = null,
  } = verifyBtnParams["Director"]["Local Company"];

  const entityTypesAccepted = ["Private Limited", "Public Limited"];
  const [chosenEntityType, setChosenEntityType] = useState(
    entityTypesAccepted[0]
  );
  const isFirstRender = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const { corporateId, companyId } = useParams();
  const formikProps = useFormikContext();
  const { verifyStatus } = useSelector((state) => state.verifyInviteSlice);
  const { organization } = useSelector((state) => state.organizationsSlice);
  const { companyRegNumber } = useSelector(
    (state) => state.transferYourBusinessSlice
  );
  const [verificationValue, setVerifyingValue] = useState(companyRegNumber);
  const [isAccessRevoked, setIsAccessRevoked] = useState(false);
  const [isAccessAllowed, setIsAccessAllowed] = useState(false);
  const [isRegNumberVerified, setIsRegNumberVerified] = useState(
    companyRegNumber ? true : false
  );
  // const { company } = useSelector((state) => state.companySlice);
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const history = useHistory();

  const { company } = useSelector((state) => state.companyDashboardSlice);
  const { corporates, getCorporatesForDashboardStatus } = useSelector(
    (state) => state.corporatesForDashboardSlice
  );

  const getVerifyResponse = async () => {
    if (companyId) {
      return dispatch(
        verify({
          data: {
            companyId: company?._id,
            verificationKey: name,
            verificationValue:
              typeof verificationValue === "string"
                ? verificationValue?.trim()
                : verificationValue,
            idType: "Local Company",
            for: "company",
          },
        })
      );
    } else if (corporateId) {
      return dispatch(
        verifyForOrganization({
          data: {
            organizationId: organization?._id,
            verificationKey: name,
            verificationValue:
              typeof verificationValue === "string"
                ? verificationValue?.trim()
                : verificationValue,
            idType: "Local Company",
            for: "organization",
          },
        })
      );
    } else {
      return dispatch(
        verifyForOrganization({
          data: {
            verificationKey: name,
            verificationValue:
              typeof verificationValue === "string"
                ? verificationValue?.trim()
                : verificationValue,
            idType: "Local Company",
            for: "organization",
          },
        })
      );
    }
  };

  const handleVerifyResponse = async (response) => {
    if (
      response?.payload?.populateData &&
      entityTypesAccepted.includes(
        response?.payload?.official?.identificationDetails?.entityType
      )
    ) {
      history.push(
        `/corporates/${response.payload.official?._id}/dashboard?transferYourBusiness`
      );
    }
    if (
      response?.payload?.populateData &&
      !entityTypesAccepted.includes(
        response?.payload?.official?.identificationDetails?.entityType
      )
    ) {
      if (response?.payload?.official?.transferYourBusinessInitiator) {
        setCompanyIsTransfered(true);
      } else {
        setChosenEntityType(
          response?.payload?.official?.identificationDetails?.entityType
        );
        setEntityTypeIsAccepted(false);
      }

      return;
    }
    if (response?.payload?.corporate?._id) {
      // Redirect to company dashboard
      history.push(`/corporates/${response.payload.corporate?._id}/dashboard`);
      return;
    }
    if (response?.payload?.initiatedToCompany) {
      setCompanyExists(true);
    }
    if (response?.payload?.sameLinkedToUser) {
      toast(
        "error",
        response?.payload?.message ?? "Something went wrong please try again."
      );
    }
    if (response?.payload?.accessDenied) {
      toast(
        "error",
        response?.payload?.message ?? "Something went wrong please try again."
      );
    }
    if (response?.payload?.inviteAlreadySent) {
      toast(
        "error",
        response?.payload?.message ?? "Something went wrong please try again."
      );
    }
    if (response?.payload?.accessRevoked) {
      setIsAccessRevoked(true);
    }
    if (response?.payload?.officialAlreadyExists) {
      toast("error", response?.payload?.message);
    }
  };

  useEffect(() => {
    if (organization?._id) {
      dispatch(
        getCorporatesForDashboard({
          companyRegNumber: organization.names.companyRegNumber,
          companyDashboard: !organization.partnershipType,
        })
      );
    }
  }, [organization?._id]);

  const handleVerify = async () => {
    if (isVerified) {
      setInviteExistingUser(true);
      return;
    }
    setIsLoading(true);
    resetForm();
    try {
      const response = await getVerifyResponse();
      await handleVerifyResponse(response);
      setShowBusinessDetails(true);
      setIsRegNumberVerified(true);
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetVerification = () => {
    setInviteExistingUser(false);
    setIsAccessAllowed(false);
    setIsAccessRevoked(false);
    setIsVerified(false);
    setIsRegNumberVerified(false);
    setShowBusinessDetails(false);
    setEntityTypeIsAccepted(true);
    setChosenEntityType(entityTypesAccepted[0]);
    resetForm();
  };

  useEffect(() => {
    return () => {
      dispatch(resetVerifyStatus());
    };
  }, []);

  return (
    <div>
      <div>
        <Formik
          enableReinitialize
          initialValues={{ [name]: verificationValue }}
          validationSchema={validationSchema}
          onSubmit={handleVerify}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            isSubmitting,
            ...verificationFormProps
          }) => {
            useEffect(() => {
              const value =
                type === "number" ? parseInt(values[name]) : values[name];
              setVerifyingValue(value);
            }, [values[name], type]);

            const handleKeyDown = (e) => {
              if (e.keyCode === 13) {
                e.preventDefault();
                handleSubmit();
              }
            };

            const handleInputChange = (e) => {
              dispatch(resetVerifyStatus());
              dispatch(setVerifiedRegNumber(e.target.value));
              verificationFormProps.setFieldValue(name, e.target.value);
            };

            const [debouncedVerificationValue] = useDebounce(
              values[name],
              1000
            );

            // verify verificationValue asynchronously
            useEffect(() => {
              if (isFirstRender.current) {
                isFirstRender.current = false;
                return;
              }
              const verifyDebouncedValue = async () => {
                const isValid = await validationSchema.isValid({
                  [name]: debouncedVerificationValue,
                });
                if (isValid) {
                  dispatch(setVerifiedRegNumber(verificationValue));
                  handleVerify();
                }
              };
              if (!verifyStatus.loaded) verifyDebouncedValue();
            }, [debouncedVerificationValue]);

            return (
              <Form onKeyDown={handleKeyDown}>
                <div className="col-span-6 sm:col-span-3 ">
                  <label
                    htmlFor={`${name}_verify_btn`}
                    className="block text-sm font-medium text-gray-700"
                  >
                    {label}
                  </label>
                  <div className="relative mt-1 rounded-md shadow-sm">
                    <Field
                      type={type}
                      name={name}
                      id={`${name}_verify_btn`}
                      autoComplete={autoComplete}
                      className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm capitalized focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none disabled:cursor-not-allowed"
                      placeholder={placeholder}
                      disabled={
                        isVerified ||
                        isAccessAllowed ||
                        isRegNumberVerified ||
                        isAccessRevoked ||
                        verifyStatus.status === "loading"
                      }
                      onChange={handleInputChange}
                    />
                    {verifyStatus.status === "loading" && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <SpinnerIcon textColor="text-gray-400" />
                      </div>
                    )}
                    {isIdAssociated !== null && debouncedVerificationValue && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 z-[29]">
                        <button type="button" onClick={handleResetVerification}>
                          <PencilIcon className="w-4 h-4 cursor-pointer text-light-blue" />
                        </button>
                      </div>
                    )}
                  </div>
                  <ValidationError
                    errors={errors}
                    touched={{ ...touched, [name]: true }}
                    name={name}
                  />
                </div>

                <div className="flex justify-between my-4">
                  {
                    <Button
                      disabled={
                        !(typeof values[name] === "string"
                          ? values[name]?.trim()
                          : values[name]) ||
                        isLoading ||
                        isIdAssociated === false ||
                        isAccessRevoked ||
                        verifyStatus.status === "loading"
                      }
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      loadingText="Verifying..."
                    >
                      {isVerified ? "Invite" : "Verify"}
                    </Button>
                  }
                  {isAccessRevoked && (
                    <Button
                      onClick={() => {
                        setInviteExistingUser(true);
                      }}
                    >
                      Invite
                    </Button>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
        {isIdAssociated === false && (
          <div className="flex flex-col my-4 space-y-2 items-between">
            <div className="text-red-500 flex items-start py-2">
              <ExclamationIcon className="w-5 h-5 mr-1" />
              <p className="block text-sm font-medium">
                We don't have such an ID in our system. Please initiate an
                invite or complete the form below.
              </p>
            </div>
          </div>
        )}
        {isAccessRevoked && (
          <div className="flex flex-col my-4 space-y-2 items-between">
            <div className="text-red-500 flex items-start py-2">
              <ExclamationIcon className="w-5 h-5 mr-1" />
              <p className="block text-sm font-medium">
                {CORPORATES.includes("Local Company") ? "Corporate" : "User"}{" "}
                exists in the system however their data can only be used once.
                Kindly initiate an invite to request reuse of their data.
              </p>
            </div>
          </div>
        )}
        {companyExists && (
          <div className="flex flex-col my-4 space-y-2 items-between">
            <div className="text-red-500 flex items-start py-2">
              <ExclamationIcon className="w-5 h-5 mr-1" />
              <p className="block text-sm font-medium">
                The company already exists in the system.
              </p>
            </div>
          </div>
        )}
        {companyIsTransfered && (
          <div className="flex flex-col my-4 space-y-2 items-between">
            <div className="text-red-500 flex items-start py-2">
              <ExclamationIcon className="w-5 h-5 mr-1" />
              <p className="block text-sm font-medium">
                An existing transaction to transfer this company has been
                initiated.
              </p>
            </div>
          </div>
        )}
        {!entityTypeIsAccepted && (
          <div className="flex flex-col my-4 space-y-2 items-between">
            <div className="text-red-500 flex items-start py-2">
              <ExclamationIcon className="w-5 h-5 mr-1" />
              <p className="block text-sm font-medium">
                The entity type "{chosenEntityType}" is not eligible for
                transfer. Only the following entity types can be transferred:{" "}
                {entityTypesAccepted.join(", ")}.
              </p>
            </div>
          </div>
        )}
      </div>
      <hr />
    </div>
  );
};

export default Verify;
